import { useState, useEffect } from "react";
import {
  Box,
  InputBase,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Grid,
  Chip,
  Divider,
} from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { useTheme } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DownloadIcon from "@mui/icons-material/Download";
import { tokens } from "../../theme";
import Header from "../../components/Header";
import { getCVpostulantes } from "../../services/postulantes.services";

const BASE_URL = "http://localhost:5003"; // Cambia por tu URL base del backend

const ESTADO_MAP = {
  0: { label: "Pendiente", color: "warning" },
  1: { label: "Aprobado",  color: "success" },
  2: { label: "Rechazado", color: "error"   },
};

// ─── Título de sección ──────────────────────────────────────────────────────────
const SectionTitle = ({ children, colors }) => (
  <Grid item xs={12}>
    <Box sx={{ mb: 0.5 }}>
      <Typography
        variant="caption"
        sx={{
          color: colors.blueAccent[300],
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: "0.08em",
          fontSize: "0.7rem",
        }}
      >
        {children}
      </Typography>
      <Divider sx={{ borderColor: colors.primary[300], mt: 0.5 }} />
    </Box>
  </Grid>
);

// ─── Campo tipo input readonly ──────────────────────────────────────────────────
const InfoField = ({ label, value, colors, xs = 6 }) => (
  <Grid item xs={12} sm={xs}>
    <Typography
      variant="caption"
      sx={{
        color: colors.greenAccent[400],
        fontWeight: 600,
        letterSpacing: "0.05em",
        textTransform: "uppercase",
        fontSize: "0.67rem",
        mb: 0.5,
        display: "block",
      }}
    >
      {label}
    </Typography>
    <Box
      sx={{
        backgroundColor: colors.primary[500],
        border: `1px solid ${colors.primary[300]}`,
        borderRadius: "6px",
        padding: "9px 14px",
        minHeight: "38px",
        display: "flex",
        alignItems: "center",
      }}
    >
      <Typography
        variant="body2"
        sx={{ color: colors.grey[100], fontSize: "0.84rem", lineHeight: 1.4 }}
      >
        {value || "—"}
      </Typography>
    </Box>
  </Grid>
);

// ─── Campo con chips (valores separados por ";") ────────────────────────────────
const ChipField = ({ label, value, colors, xs = 6 }) => {
  const items = value ? value.split(";").map((v) => v.trim()).filter(Boolean) : [];
  return (
    <Grid item xs={12} sm={xs}>
      <Typography
        variant="caption"
        sx={{
          color: colors.greenAccent[400],
          fontWeight: 600,
          letterSpacing: "0.05em",
          textTransform: "uppercase",
          fontSize: "0.67rem",
          mb: 0.5,
          display: "block",
        }}
      >
        {label}
      </Typography>
      <Box
        sx={{
          backgroundColor: colors.primary[500],
          border: `1px solid ${colors.primary[300]}`,
          borderRadius: "6px",
          padding: "7px 12px",
          minHeight: "38px",
          display: "flex",
          flexWrap: "wrap",
          gap: "6px",
          alignItems: "center",
        }}
      >
        {items.length > 0 ? (
          items.map((item, i) => (
            <Chip
              key={i}
              label={item}
              size="small"
              sx={{
                backgroundColor: colors.blueAccent[700],
                color: colors.grey[100],
                fontSize: "0.75rem",
                height: 24,
              }}
            />
          ))
        ) : (
          <Typography variant="body2" sx={{ color: colors.grey[400], fontSize: "0.84rem" }}>
            —
          </Typography>
        )}
      </Box>
    </Grid>
  );
};

// ─── Modal de detalle ───────────────────────────────────────────────────────────
const DetalleModal = ({ open, onClose, row, colors }) => {
  if (!row) return null;

  const estado = ESTADO_MAP[row.Estado] ?? { label: `Estado ${row.Estado}`, color: "default" };
  const adjuntoUrl = row.adjunto ? `${BASE_URL}${row.adjunto}` : null;
  const isPdf = adjuntoUrl?.toLowerCase().endsWith(".pdf");

  const formatDate = (iso) => {
    if (!iso) return "—";
    return new Date(iso).toLocaleDateString("es-PY", {
      day: "2-digit", month: "2-digit", year: "numeric",
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: colors.primary[400],
          backgroundImage: "none",
          border: `1px solid ${colors.primary[300]}`,
          borderRadius: "12px",
        },
      }}
    >
      {/* Header del modal */}
      <DialogTitle
        sx={{
          backgroundColor: colors.blueAccent[900],
          color: colors.grey[100],
          fontWeight: 700,
          fontSize: "1rem",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 2,
        }}
      >
        Detalles del Postulante {row.Nombres} {row.Apellidos}
        <Chip label={estado.label} color={estado.color} size="small" sx={{ fontWeight: 600 }} />
      </DialogTitle>

      <DialogContent sx={{ pt: 3, pb: 2, px: 3 }}>
        <Grid container spacing={2}>

          {/* ── Datos Personales ── */}
          <SectionTitle colors={colors}>Datos Personales</SectionTitle>
          <InfoField label="Cédula"     value={row.cedula}    colors={colors} xs={4} />
          <ChipField label="Teléfono"   value={row.telefono}  colors={colors} xs={4} />
          <InfoField label="Sexo"       value={row.sexo}      colors={colors} xs={4} />
          <InfoField label="Dirección"  value={row.direccion} colors={colors} xs={12} />
          <InfoField label="Fecha Recepción" value={formatDate(row.fecha_recepcion)} colors={colors} xs={6} />
          <ChipField label="Puesto(s)"   value={row.Puesto} colors={colors} xs={6} />
          <ChipField label="Experiencia" value={row.xp}     colors={colors} xs={6} />
          <ChipField label="Referencia Laboral"  value={row.ref}     colors={colors} xs={8} />
          <ChipField label="Teléfono Referencia" value={row.tel_ref} colors={colors} xs={4} />

          {/* ── Estado ── */}
          <InfoField label="Observación" value={row.obs_estado} colors={colors} xs={12} />

          {/* ── Archivo Adjunto ── */}
          {adjuntoUrl && (
            <>
              <SectionTitle colors={colors}>Currículum Vitae</SectionTitle>

              {/* Barra con nombre + botones — ancho completo */}
              <Grid item xs={12}>
                <Box
                  sx={{
                    backgroundColor: colors.primary[500],
                    border: `1px solid ${colors.primary[300]}`,
                    borderRadius: "6px",
                    padding: "10px 14px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                    flexWrap: "wrap",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: colors.grey[200],
                      fontSize: "0.8rem",
                      wordBreak: "break-all",
                      flex: 1,
                    }}
                  >
                    {row.adjunto.split("/").pop()}
                  </Typography>

                  <Box display="flex" gap={1}>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<VisibilityIcon fontSize="small" />}
                      onClick={() => window.open(adjuntoUrl, "_blank")}
                      sx={{
                        borderColor: colors.blueAccent[400],
                        color: colors.blueAccent[300],
                        textTransform: "none",
                        fontSize: "0.78rem",
                        "&:hover": {
                          borderColor: colors.blueAccent[300],
                          backgroundColor: colors.blueAccent[900],
                        },
                      }}
                    >
                      Visualizar
                    </Button>

                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<DownloadIcon fontSize="small" />}
                      component="a"
                      href={adjuntoUrl}
                      download
                      sx={{
                        backgroundColor: colors.greenAccent[600],
                        color: "#fff",
                        textTransform: "none",
                        fontSize: "0.78rem",
                        "&:hover": { backgroundColor: colors.greenAccent[700] },
                      }}
                    >
                      Descargar
                    </Button>
                  </Box>
                </Box>
              </Grid>

              {/* Preview — ancho completo */}
              <Grid item xs={12}>
                <Box
                  sx={{
                    border: `1px solid ${colors.primary[300]}`,
                    borderRadius: "6px",
                    overflow: "hidden",
                    backgroundColor: colors.primary[500],
                    width: "100%",
                  }}
                >
                  {isPdf ? (
                    <iframe
                      src={adjuntoUrl}
                      width="100%"
                      height="480px"
                      style={{ border: "none", display: "block" }}
                      title="Vista previa PDF"
                    />
                  ) : (
                    <Box sx={{ textAlign: "center", p: 1 }}>
                      <img
                        src={adjuntoUrl}
                        alt="Adjunto"
                        style={{ maxWidth: "100%", maxHeight: 420, borderRadius: 4 }}
                      />
                    </Box>
                  )}
                </Box>
              </Grid>
            </>
          )}

        </Grid>
      </DialogContent>

      <DialogActions sx={{ backgroundColor: colors.blueAccent[900], px: 3, py: 1.5 }}>
        <Button
          onClick={onClose}
          sx={{
            backgroundColor: 'rgb(61, 165, 138)',
            color: colors.grey[200],
            textTransform: "none",
            "&:hover": { backgroundColor: colors.primary[400] },
          }}
        >
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Vista principal ────────────────────────────────────────────────────────────
const CvPostulanteView = () => {
  const [data, setData] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [filteredData, setFilteredData] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const columns = [
    { field: "id", headerName: "ID", flex: 0.4 },
    {
      field: "Nombres",
      headerName: "Nombre Completo",
      flex: 1.8,
      cellClassName: "name-column--cell",
      valueGetter: (params) => `${params.row.Nombres} ${params.row.Apellidos}`,
    },
    {
      field: "cedula",
      headerName: "Cédula",
      flex: 1,
      headerAlign: "left",
      align: "left",
    },
    {
      field: "telefono",
      headerName: "Teléfono",
      flex: 1,
      headerAlign: "left",
      align: "left",
    },
    {
      field: "Puesto",
      headerName: "Puesto",
      flex: 1,
      headerAlign: "left",
      align: "left",
      valueGetter: (params) =>
        params.row.Puesto
          ? params.row.Puesto.split(";").map((p) => p.trim()).join(" / ")
          : "—",
    },
    {
      field: "fecha_recepcion",
      headerName: "Fecha Recepción",
      flex: 1.2,
      headerAlign: "left",
      align: "left",
      valueGetter: (params) => {
        if (!params.row.fecha_recepcion) return "—";
        return new Date(params.row.fecha_recepcion).toLocaleDateString("es-PY", {
          day: "2-digit", month: "2-digit", year: "numeric",
        });
      },
    },
    {
      field: "Estado",
      headerName: "Estado",
      flex: 1,
      headerAlign: "left",
      align: "left",
      valueGetter: (params) =>
        (ESTADO_MAP[params.row.Estado] ?? { label: `Estado ${params.row.Estado}` }).label,
    },
    {
      field: "detalles",
      headerName: "Detalles",
      flex: 0.6,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <IconButton
          size="small"
          onClick={() => { setSelectedRow(params.row); setModalOpen(true); }}
          sx={{ color: colors.blueAccent[300] }}
          title="Ver detalle"
        >
          <VisibilityIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  useEffect(() => {
    const fetchPostulantes = async () => {
      try {
        const response = await getCVpostulantes();
        setData(response);
        setFilteredData(response);
      } catch (error) {
        console.error("Error al obtener los postulantes:", error);
      }
    };
    fetchPostulantes();
  }, []);

  const handleSearchChange = (event) => {
    const { value } = event.target;
    setSearchValue(value);
    const q = value.toLowerCase();
    const filtered = data.filter((row) => {
      const estado = (ESTADO_MAP[row.Estado]?.label ?? "").toLowerCase();
      return (
        `${row.Nombres} ${row.Apellidos}`.toLowerCase().includes(q) ||
        row.cedula.includes(q) ||
        (row.Puesto ?? "").toLowerCase().includes(q) ||
        row.telefono.includes(q) ||
        estado.includes(q)
      );
    });
    setFilteredData(filtered);
  };

  return (
    <Box m="20px">
      <Box display="flex" justifyContent="space-between" p={0}>
        <Header title="CV Postulantes" subtitle="Lista de Postulantes Recibidos" />
        <Box display="flex" alignItems="center">
          <Box
            display="flex"
            backgroundColor={colors.primary[400]}
            borderRadius="3px"
            height={"40%"}
            width={"90%"}
            ml={1}
          >
            <InputBase
              sx={{ ml: 2, flex: 1 }}
              placeholder="Buscar"
              value={searchValue}
              onChange={handleSearchChange}
            />
            <IconButton type="button" sx={{ p: 1 }}>
              <SearchIcon />
            </IconButton>
          </Box>
        </Box>
      </Box>

      <Box
        m="40px 0 0 0"
        height="75vh"
        sx={{
          "& .MuiDataGrid-root": { border: "none" },
          "& .MuiDataGrid-cell": { borderBottom: "none" },
          "& .name-column--cell": { color: colors.greenAccent[300] },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: colors.blueAccent[900],
            borderBottom: "none",
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: colors.primary[400],
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "none",
            backgroundColor: colors.blueAccent[900],
          },
          "& .MuiCheckbox-root": {
            color: `${colors.greenAccent[200]} !important`,
          },
          "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
            color: `${colors.grey[100]} !important`,
          },
        }}
      >
        <DataGrid
          rows={filteredData}
          columns={columns}
          components={{ Toolbar: GridToolbar }}
          disableMultipleSelection={true}
          getRowId={(row) => row.id}
        />
      </Box>

      <DetalleModal
        open={modalOpen}
        onClose={() => { setModalOpen(false); setSelectedRow(null); }}
        row={selectedRow}
        colors={colors}
      />
    </Box>
  );
};

export default CvPostulanteView;